import React, { useState, useEffect } from 'react';
import { getFromHub, postToHub } from '../network/actions';
import { InputText } from 'primereact/inputtext';

function MfaSetupStep({ onEnrolled, onBack }) {
    const [qrData, setQrData] = useState(null);
    const [totp, setTotp] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [showSecret, setShowSecret] = useState(false);

    useEffect(() => {
        async function fetchSetup() {
            try {
                const data = await getFromHub('authentication/mfa/enrollment/setup');
                if (data) {
                    setQrData(data)
                } else {
                    setError('Failed to load MFA setup. Please try again.')
                }
            } catch {
                setError('Failed to load MFA setup. Please try again.')
            } finally {
                setFetching(false)
            }
        }
        fetchSetup();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (totp === null || totp.length !== 6) {
            setError('Please enter a valid 6-digit code.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const result = await postToHub('authentication/mfa/enrollment/confirm', {
                totp: Number(totp)
            });
            if (result === true) {
                onEnrolled();
            } else {
                setError('Incorrect code. Please try again.');
            }
        } catch {
            setError('Verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex flex-col items-center py-8">
                <p className="text-gray-500 text-sm">Loading MFA setup…</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Set up MFA</h2>
                <p className="text-xs text-gray-500">Scan QR code with Google Authenticator or similar</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-800">
                <strong>Step 1</strong> — Open your authenticator app and scan the QR code below.<br />
                <strong>Step 2</strong> — Enter the 6-digit code shown in the app.
            </div>

            {qrData && (
                <div className="flex flex-col items-center mb-4">
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-3 shadow-sm">
                        <img
                            src={qrData.qrCodeImage}
                            alt="MFA QR Code"
                            className="w-44 h-44"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowSecret(!showSecret)}
                        className="mt-2 text-xs text-blue-600 hover:underline"
                    >
                        {showSecret ? 'Hide' : 'Can\'t scan? Enter key manually'}
                    </button>
                    {showSecret && (
                        <div className="mt-2 bg-gray-100 rounded-lg px-4 py-2 text-center">
                            <p className="text-xs text-gray-500 mb-1">Account: <strong>{qrData.issuer}</strong></p>
                            <p className="font-mono text-sm font-bold tracking-widest text-gray-800 break-all">
                                {qrData.secret}
                            </p>
                        </div>
                    )}
                </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                {qrData && (
                    <div>
                        <label htmlFor="totp-setup" className="block text-xs font-medium text-gray-600 mb-1">
                            Verification code
                        </label>
                        <InputText
                            id="totp-setup"
                            value={totp}
                            onChange={(e) => {
                                setError('');
                                setTotp(e.target.value.replace(/\D/g, ''));
                            }}
                            maxLength={6}
                            autoComplete="off"
                            className="w-full text-center text-2xl"
                        />
                    </div>
                )}

                {error &&
                    <div className="text-red-700">
                        {error}
                    </div>
                }

                <button
                    type="submit"
                    disabled={loading || totp.length !== 6}
                    className="w-full bg-blue-800 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors"
                >
                    {loading ? 'Verifying…' : 'Verify & Activate'}
                </button>

                <button
                    type="button"
                    onClick={onBack}
                    className="text-sm text-gray-500 hover:text-gray-700 text-center"
                >
                    ← Back to sign in
                </button>
            </form>
        </div>
    );
}

export default MfaSetupStep;
